import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import {
  isPushSupported,
  getPushPermission,
  requestPushPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentPushSubscription,
} from '../lib/pushNotifications';

interface Props {
  userId: string;
}

const PushNotificationToggle: React.FC<Props> = ({ userId }) => {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    if (!isPushSupported()) {
      setLoading(false);
      return;
    }
    setPermission(getPushPermission());
    getCurrentPushSubscription().then(sub => {
      setSubscribed(!!sub);
      setLoading(false);
    });
  }, []);

  const handleToggle = async () => {
    setLoading(true);
    if (subscribed) {
      await unsubscribeFromPush(userId);
      setSubscribed(false);
    } else {
      if (permission !== 'granted') {
        const result = await requestPushPermission();
        setPermission(result);
        if (result !== 'granted') {
          setLoading(false);
          return;
        }
      }
      const success = await subscribeToPush(userId);
      setSubscribed(success);
    }
    setLoading(false);
  };

  if (!isPushSupported()) {
    return (
      <div className="flex items-center gap-3 p-4 bg-zinc-50 rounded-xl border border-zinc-200">
        <BellOff className="w-5 h-5 text-zinc-400" />
        <div>
          <p className="text-sm font-medium text-zinc-700">Push-Benachrichtigungen</p>
          <p className="text-xs text-zinc-400">Nicht unterstützt in diesem Browser</p>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="flex items-center gap-3 p-4 bg-zinc-50 rounded-xl border border-zinc-200">
        <BellOff className="w-5 h-5 text-red-400" />
        <div>
          <p className="text-sm font-medium text-zinc-700">Push-Benachrichtigungen</p>
          <p className="text-xs text-zinc-400">Blockiert – bitte in den Browser-Einstellungen erlauben</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-zinc-200">
      <div className="flex items-center gap-3">
        {subscribed
          ? <Bell className="w-5 h-5 text-zinc-900" />
          : <BellOff className="w-5 h-5 text-zinc-400" />
        }
        <div>
          <p className="text-sm font-medium text-zinc-700">Push-Benachrichtigungen</p>
          <p className="text-xs text-zinc-400">
            {subscribed ? 'Aktiv – du wirst bei neuen Anfragen benachrichtigt' : 'Inaktiv'}
          </p>
        </div>
      </div>
      <button
        onClick={handleToggle}
        disabled={loading}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          subscribed ? 'bg-zinc-900' : 'bg-zinc-200'
        } disabled:opacity-50`}
      >
        {loading
          ? <Loader2 className="w-3 h-3 animate-spin text-white mx-auto" />
          : <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              subscribed ? 'translate-x-6' : 'translate-x-1'
            }`} />
        }
      </button>
    </div>
  );
};

export default PushNotificationToggle;
